'use client';

import { useTranslation } from 'react-i18next';
import { Button, TextButton } from '@galapagos/design-system/web';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useFinishSignupFlow } from '../hooks/useFinishSignupFlow';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useStepHeading } from '../hooks/useStepHeading';

/**
 * A step that ends the flow: a title and a line of explanation, then the two
 * ways to reach a person the footer also offers, so the page is never a dead
 * end: Book a call, or an email to the support address.
 */
function TerminalStep({ title, body }: { title: string; body: string }) {
  const { t } = useTranslation('signup');
  const { t: tCommon } = useTranslation('common');
  const { goToBookACall } = useSignupNavigation();
  const email = tCommon('siteFooter.emailAddress');
  // Every flow page refills the sponsor params from its own URL.
  useLegacySignupParams();
  // The flow ends here, so the answers kept for this tab go.
  useFinishSignupFlow();
  const headingRef = useStepHeading(title);
  ensureSignupStepStyle();
  return (
    <SignupShell>
      <div className="signup-step">
        <PageTitle title={title} description={body} headingRef={headingRef} />
        {/* The step's one status announcer; a terminal step has nothing more to say. */}
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-status" />
        <FormActions>
          <Button
            size="L"
            type="Primary"
            label={t('ageStop.bookCall')}
            onPress={goToBookACall}
            actionId="signup.age-stop-book-call.activate"
            data-testid="signup-age-stop-book-call"
          />
          <TextButton
            label={t('ageStop.emailUs')}
            actionId="signup.age-stop-email.activate"
            data-testid="signup-age-stop-email"
            onPress={() => window.location.assign(`mailto:${email}`)}
          />
        </FormActions>
      </div>
    </SignupShell>
  );
}

function UnderAgeStep() {
  const { t } = useTranslation('signup');
  return <TerminalStep title={t('ageStop.underAge.title')} body={t('ageStop.underAge.body')} />;
}

function MinorUnsupportedStep() {
  const { t } = useTranslation('signup');
  return (
    <TerminalStep
      title={t('ageStop.minorUnsupported.title')}
      body={t('ageStop.minorUnsupported.body')}
    />
  );
}

/** `/under-age/`: the server's gate says the member is below every program's minimum age. */
export function UnderAgeStepScreen() {
  return <UnderAgeStep />;
}

/** `/minor-unsupported/`: the server's gate says the member is a minor and the sponsor does not cover minors. */
export function MinorUnsupportedStepScreen() {
  return <MinorUnsupportedStep />;
}
